import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";

import { Button } from "@chartvn/ui/button";

import { Can } from "../../features/auth/can";
import { SummaryScreen } from "../../features/summaries/components/summary-screen";

/**
 * One summary on its own screen (`RPM-103`, `CR-45` R5, `J-602`): "a summary opens on a screen of its
 * own with a button back to the list, not below the list".
 *
 * NOT NESTED UNDER THE LIST. The trailing `_` in `summaries_` takes this route out of the list's
 * layout, so nothing of the list renders here (row 12). The address names the summary by its id and
 * nothing else (row 14): a reload reads the same summary again, and no name or date is in it.
 *
 * BACK TO SUMMARIES steps back to the list as she left it — filters, pages and the row opened are in
 * that entry's address (row 13). Only the list opens a summary, so the entry before this one is the
 * list; a summary opened by its address alone has none, and Back opens the list unfiltered.
 */
function SummaryRoute() {
  const { summaryId } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  return (
    <div className="flex flex-col gap-4 pt-4">
      <div>
        <Button
          type="button"
          variant="outline"
          data-testid="summaries-back"
          className="min-h-11"
          onClick={() => {
            if (canGoBack) router.history.back();
            else void router.navigate({ to: "/summaries" });
          }}
        >
          Back to summaries
        </Button>
      </div>
      <SummaryScreen key={summaryId} summaryId={summaryId} gate={Can} />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/summaries_/$summaryId")({
  // THE PAGE HEADER IS THE SHELL'S: it reads this name and moves focus to its one h1 on arrival.
  staticData: { screenName: "Summary" },
  component: SummaryRoute,
});
